import { forwardRef, useEffect, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';
import { EASE, getGsap, hasFinePointer } from '@/lib/motion';
import { canAnimate, glueDashes } from './heroEnv';
import './hero.css';

// HeroHeadline — the kinetic headline (immersive redesign, 2026-09-24).
//
// ONE <h1>, the two existing locale lines inside it, exactly as before (the
// tests read its text as "What do you wantto make today?": the two lines are
// block spans with nothing between them). The h1 carries an aria-label with
// the proper space, so a screen reader hears one clean sentence even while
// the first line is split into letters.
//
// The reveal: line 1 is split into characters (GSAP SplitText) that rise and
// unblur in a stagger; line 2 keeps its gradient, so it moves as one piece
// (per-letter transforms break background-clip:text) and then a light band
// sweeps across it. The split is reverted the moment the reveal ends, so the
// DOM goes back to plain text. Remount (key) to replay: HeroSection keys this
// on the stop AND the text, so a language switch replays it too — and never
// asks React to patch text that SplitText replaced.
//
// Reduced motion, jsdom and no-JS all get the finished state, untouched.

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// The badge breaks only between its clauses (2026-09-25), the way the tool
// cards' meta lines do: at 320px the Spanish studio badge split 'con / IA',
// and its second line moved the headline 16px on every switch to a stop with
// a one-line badge. Each clause is held together with no-break spaces, and
// so is the space before the separator, so a wrap lands after the dash, not
// before it. A badge with no separator is left alone.
const SEPARATORS = [' — ', ' · '];
function keepClauses(text: string): string {
  const sep = SEPARATORS.find((x) => text.includes(x));
  if (!sep) return text;
  const glue = `\u00a0${sep.trimStart()}`;
  return text.split(sep).map((clause) => clause.replace(/ /g, '\u00a0')).join(glue);
}

export interface HeroHeadlineProps {
  id: 'welcome' | 'storybook' | 'chopbox' | 'studio';
  badge: string;
  icon: ReactNode;
  line1: string;
  line2: string;
  subtitle: string;
  /** font-size classes for the h1 (each headline has its own length). */
  titleClass: string;
  /** extra classes for the subtitle (the studio stops left-align and
   *  tighten it under their phone kicker). */
  subtitleClass?: string;
  /** Welcome only: the stage the headline drifts up and fades against. */
  parallaxStage?: RefObject<HTMLElement>;
  /** Studio stops: under 640px the h1 is a left-aligned kicker, and the
   *  tool's own pitch headline right below it carries the display size. */
  kicker?: boolean;
}

export const HeroHeadline = forwardRef<HTMLDivElement, HeroHeadlineProps>(function HeroHeadline(
  { id, badge, icon, line1, line2, subtitle, titleClass, subtitleClass = '', parallaxStage, kicker = false },
  forwarded,
) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const setRoot = (el: HTMLDivElement | null) => {
    rootRef.current = el;
    if (typeof forwarded === 'function') forwarded(el);
    else if (forwarded) forwarded.current = el;
  };

  useIsoLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !canAnimate()) return undefined;
    const { gsap, SplitText } = getGsap();
    const badgeEl = root.querySelector<HTMLElement>('[data-hero-badge]');
    const l1 = root.querySelector<HTMLElement>('[data-hero-line="1"]');
    const l2 = root.querySelector<HTMLElement>('[data-hero-line="2"]');
    const sub = root.querySelector<HTMLElement>('[data-hero-sub]');
    if (!l1 || !l2) return undefined;

    // Phones get a lighter blur: it is the costly part of the reveal.
    const soft = hasFinePointer() ? 1 : 0.6;
    let tl: gsap.core.Timeline | null = null;
    const ctx = gsap.context(() => {
      const split = SplitText.create(l1, { type: 'words,chars', aria: 'hidden' });
      tl = gsap.timeline({
        defaults: { ease: EASE.out },
        onComplete: () => {
          split.revert();
          gsap.set([l2], { clearProps: 'transform,filter,opacity' });
        },
      });
      if (badgeEl) tl.from(badgeEl, { y: 14, opacity: 0, filter: 'blur(6px)', duration: 0.9, clearProps: 'filter' }, 0.05);
      tl.from(split.chars, {
        yPercent: 60,
        opacity: 0,
        filter: `blur(${14 * soft}px)`,
        duration: 1.15,
        stagger: { each: 0.026, from: 'start' },
      }, 0.12);
      tl.from(l2, { y: '0.38em', opacity: 0, filter: `blur(${16 * soft}px)`, scale: 0.985, duration: 1.25 }, 0.34);
      tl.fromTo(l2, { '--hero-shine': '150%' }, { '--hero-shine': '-50%', duration: 1.5, ease: EASE.inOut }, 0.85);
      if (sub) tl.from(sub, { y: 18, opacity: 0, duration: 1.1, clearProps: 'transform' }, 0.5);
    }, root);
    // Never leave the headline hidden: if the main thread is starved (a slow
    // phone mid-load), GSAP's lag smoothing stretches the reveal out, so a
    // plain timer jumps it to the end.
    const failsafe = window.setTimeout(() => {
      if (tl && tl.progress() < 1) tl.progress(1);
    }, 3200);
    return () => {
      window.clearTimeout(failsafe);
      ctx.revert();
    };
  }, []);

  // Welcome: as the stage scrolls away the headline drifts up and dims a
  // little, scrubbed to the scroll (the 3D screens dolly past behind it).
  useEffect(() => {
    const stage = parallaxStage?.current;
    const root = rootRef.current;
    if (!stage || !root || !canAnimate()) return undefined;
    const { gsap } = getGsap();
    const tween = gsap.to(root, {
      y: () => -Math.min(110, window.innerHeight * 0.12),
      opacity: 0.25,
      ease: 'none',
      scrollTrigger: { trigger: stage, start: 'top top', end: 'bottom top', scrub: 0.6, invalidateOnRefresh: true },
    });
    return () => {
      tween.scrollTrigger?.kill();
      tween.kill();
      gsap.set(root, { clearProps: 'transform,opacity' });
    };
  }, [parallaxStage]);

  return (
    <div
      ref={setRoot}
      data-testid={`hero-headline-${id}`}
      className={`relative mx-auto w-full max-w-5xl text-center${kicker ? ' max-sm:text-left' : ''}`}
    >
      {/* The badge wraps rather than truncates (2026-09-24 review): the
          Spanish studio line is the owner's claim and lost its last word to
          an ellipsis at 320px. It wraps only between clauses (keepClauses),
          and under 360px it steps down to 11px so the Spanish line holds one
          row. The radius reads as a pill on one line and a rounded card on
          two. */}
      <div
        data-hero-badge=""
        className="hero-hairline relative mb-4 inline-flex max-w-full items-center gap-2 rounded-[1.1rem] bg-[linear-gradient(180deg,rgba(255,255,255,0.075),rgba(255,255,255,0.02))] px-3.5 py-1.5 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.6)] max-[359px]:px-3 sm:mb-6 sm:px-4"
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-primary [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
        <span className="min-w-0 text-balance text-center text-[0.85rem] font-medium leading-snug tracking-wide text-primary [overflow-wrap:anywhere] max-[359px]:text-[11px] max-[359px]:tracking-normal sm:text-sm">{keepClauses(badge)}</span>
      </div>

      {/* A kicker on a phone (2026-09-25 review): the studio line used to be
          a second gradient display headline, centred, right above the tool's
          left-aligned pitch headline. */}
      <h1
        aria-label={`${line1} ${line2}`}
        className={`${titleClass} mb-4 font-display font-bold leading-[1.02] tracking-[-0.035em] text-foreground sm:mb-6${kicker ? ' max-sm:mb-3 max-sm:leading-[1.12] max-sm:tracking-[-0.02em]' : ''}`}
      >
        <span className="block text-balance" data-hero-line="1">{line1}</span>
        <span className="block text-balance">
          <span data-hero-line="2" className="gradient-text hero-shine -mb-[0.12em] inline-block pb-[0.12em] text-balance">
            {line2}
          </span>
        </span>
      </h1>

      {/* 15px/1.5 on phones: a line shorter than 15.6/1.58, which gives the
          screens band and the peeking tool cards their room. Balanced, so the
          last line is never a lone 'from there.' Dashes glued to the word
          before them (round 5b): the studio subtitle, back on phones, opened
          a line on '— no shoot' at 360. */}
      <p
        data-hero-sub=""
        className={`mx-auto max-w-[36rem] text-balance text-[15px] leading-[1.5] text-white/70 sm:text-lg sm:leading-[1.58] 2xl:max-w-2xl ${subtitleClass}`}
      >
        {glueDashes(subtitle)}
      </p>
    </div>
  );
});
